import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2, ArrowLeft, Mail, Save, RefreshCw, Copy, RotateCcw, UserCheck, UserX, MapPin, AlertTriangle, Send, ExternalLink, Clock, CheckCircle2, XCircle, FileDown, MessageSquareText } from "lucide-react";
import { toast } from "sonner";

import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, unwrap, API_BASE, getToken } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { formatDate, formatDateTime } from "@/lib/format";
import { Avatar } from "@/pages/admin/AdminCleaners";
import CheckInMap from "@/components/admin/CheckInMap";
import RefundModal from "@/components/admin/RefundModal";
import AdminWorkPhotos from "@/components/admin/AdminWorkPhotos";
import CommissionBreakdown from "@/components/admin/CommissionBreakdown";

const STATUSES = ["pending_payment", "paid", "assigned", "in_progress", "completed", "cancelled", "refunded", "scheduled", "pending_coordination", "coordination_confirmed", "coordination_cancelled"];

// Only charged bookings have a payment receipt (mirrors RECEIPT_STATUSES on the backend).
const RECEIPT_STATUSES = ["paid", "assigned", "in_progress", "completed", "refunded"];

const STATUS_BADGE = {
  pending_payment: "bg-amber-50 text-amber-700 border-amber-200",
  paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  scheduled: "bg-cyan-50 text-cyan-700 border-cyan-200",
  assigned: "bg-sky-50 text-sky-700 border-sky-200",
  in_progress: "bg-indigo-50 text-indigo-700 border-indigo-200",
  completed: "bg-teal-50 text-teal-700 border-teal-200",
  cancelled: "bg-stone-100 text-stone-600 border-stone-200",
  refunded: "bg-rose-50 text-rose-700 border-rose-200",
  pending_coordination: "bg-amber-50 text-amber-700 border-amber-200",
  coordination_confirmed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  coordination_cancelled: "bg-stone-100 text-stone-600 border-stone-200",
};

const TIME_SLOT_LABELS = {
  en: {
    morning: "Morning (7 AM – 11 AM)",
    midday: "Midday (11 AM – 2 PM)",
    afternoon: "Afternoon (2 PM – 5 PM)",
    evening: "Evening (5 PM – 8 PM)",
    night: "Night (8 PM – 10 PM)",
    flexible: "Flexible — anytime",
  },
  es: {
    morning: "Mañana (7 AM – 11 AM)",
    midday: "Mediodía (11 AM – 2 PM)",
    afternoon: "Tarde (2 PM – 5 PM)",
    evening: "Tarde-Noche (5 PM – 8 PM)",
    night: "Noche (8 PM – 10 PM)",
    flexible: "Flexible — cuando puedas",
  },
};
const timeSlotLabel = (slug, locale) => (TIME_SLOT_LABELS[locale] || TIME_SLOT_LABELS.en)[slug] || (slug || "—");

export default function AdminBookingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { locale } = useI18n();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [refundModalOpen, setRefundModalOpen] = useState(false);
  const [cleaners, setCleaners] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [notifying, setNotifying] = useState(false);
  const [resendingLink, setResendingLink] = useState(false);
  const [copyingLink, setCopyingLink] = useState(false);
  const [downloadingReceipt, setDownloadingReceipt] = useState(false);
  const [resendingReceipt, setResendingReceipt] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/admin/bookings/${id}`);
      const b = unwrap(r);
      setBooking(b);
      setNotes(b.admin_notes || "");
    } catch {
      toast.error("Could not load booking");
    } finally {
      setLoading(false);
    }
  }, [id]);

  /* eslint-disable */
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/admin/cleaners?is_active=true").then((r) => setCleaners(unwrap(r)?.items || [])).catch(() => {}); }, []);
  /* eslint-enable */

  const assignCleaner = async (cleanerId) => {
    setAssigning(true);
    try {
      await api.post(`/admin/bookings/${id}/assign`, { cleaner_id: cleanerId });
      toast.success("Cleaner assigned");
      setPickerOpen(false);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail?.message || "Could not assign"); }
    finally { setAssigning(false); }
  };

  const unassignCleaner = async () => {
    const name = booking?.cleaner?.full_name || "this cleaner";
    if (!window.confirm(`Are you sure you want to unassign ${name} from this booking? The booking will return to "Paid" status and need to be reassigned.`)) return;
    try {
      await api.post(`/admin/bookings/${id}/unassign`);
      toast.success("Cleaner unassigned — booking moved back to Paid");
      load();
    } catch { toast.error("Could not unassign"); }
  };

  const notifyCustomerCleaner = async () => {
    setNotifying(true);
    try {
      await api.post(`/admin/bookings/${id}/notify-cleaner`);
      toast.success("Customer notified with cleaner details");
    } catch { toast.error("Could not send"); }
    finally { setNotifying(false); }
  };

  const changeStatus = async (newStatus) => {
    if (newStatus === "refunded") { setRefundModalOpen(true); return; }
    try {
      await api.patch(`/admin/bookings/${id}/status`, { status: newStatus, notify_customer: true });
      toast.success(`Status changed to ${newStatus.replace("_", " ")}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || "Failed");
    }
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await api.patch(`/admin/bookings/${id}/notes`, { admin_notes: notes });
      toast.success("Notes saved");
    } catch {
      toast.error("Could not save notes");
    } finally {
      setSavingNotes(false);
    }
  };

  const notifyCustomer = async () => {
    try {
      await api.post(`/admin/bookings/${id}/notify`);
      toast.success("Email sent (or logged if email transport is in log mode)");
    } catch { toast.error("Could not send"); }
  };

  const duplicate = async () => {
    try {
      const r = await api.post(`/admin/bookings/${id}/duplicate`);
      const d = unwrap(r);
      toast.success(`Created ${d.booking_number}`);
      navigate(`/admin/bookings/${d.id}`);
    } catch { toast.error("Duplicate failed"); }
  };

  const resendPaymentLink = async () => {
    setResendingLink(true);
    try {
      const r = await api.post(`/admin/bookings/${id}/resend-payment-link`);
      const d = unwrap(r);
      toast.success(`Payment reminder sent to ${d.customer_email}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || "Could not send the reminder");
    } finally {
      setResendingLink(false);
    }
  };

  const copyPaymentLink = async () => {
    setCopyingLink(true);
    try {
      const r = await api.post(`/admin/bookings/${id}/payment-link`);
      const d = unwrap(r);
      await navigator.clipboard.writeText(d.url);
      toast.success("Payment link copied to clipboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || "Could not copy the link");
    } finally {
      setCopyingLink(false);
    }
  };

  const downloadReceipt = async () => {
    setDownloadingReceipt(true);
    try {
      const res = await fetch(`${API_BASE}/admin/bookings/${id}/receipt.pdf`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error("failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `everclean-receipt-${booking.booking_number}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      toast.error("Could not download the receipt");
    } finally {
      setDownloadingReceipt(false);
    }
  };

  const resendReceipt = async () => {
    setResendingReceipt(true);
    try {
      const r = await api.post(`/admin/bookings/${id}/resend-receipt`);
      const d = unwrap(r);
      toast.success(d?.sent ? `Receipt re-sent to ${d.customer_email}` : "Receipt email was not sent — check email settings or the logs");
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || "Could not resend the receipt");
    } finally {
      setResendingReceipt(false);
    }
  };

  if (loading) return <div className="grid place-items-center py-16"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  if (!booking) return null;

  const canRefund = ["paid", "assigned", "in_progress", "completed"].includes(booking.status)
    && (booking.payment?.stripe_payment_intent_id || booking.payment?.stripe_session_id)
    && !booking.refund;

  return (
    <div className="space-y-5" data-testid="admin-booking-detail">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="text-sm text-[#4B5563] hover:text-[#0EA5A4] flex items-center gap-1" data-testid="back-link">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
      </div>

      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tighter">{booking.booking_number}</h1>
          <div className="mt-1 flex items-center gap-2 text-sm text-[#4B5563]">
            <Badge className={`border ${STATUS_BADGE[booking.status]} font-medium capitalize`}>{booking.status.replace("_", " ")}</Badge>
            <span>· {formatDateTime(booking.created_at, locale)}</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <select value={booking.status} onChange={(e) => changeStatus(e.target.value)}
            className="h-9 rounded-xl border border-[#E5E7EB] px-3 text-sm bg-white" data-testid="status-select">
            {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
          </select>
          <Button onClick={notifyCustomer} variant="outline" className="rounded-xl" data-testid="notify-btn">
            <Mail className="w-4 h-4 mr-2" /> Notify
          </Button>
          <Button onClick={duplicate} variant="outline" className="rounded-xl" data-testid="duplicate-btn">
            <Copy className="w-4 h-4 mr-2" /> Duplicate
          </Button>
          {RECEIPT_STATUSES.includes(booking.status) && (
            <>
              <Button onClick={downloadReceipt} disabled={downloadingReceipt} variant="outline" className="rounded-xl" data-testid="download-receipt-btn">
                {downloadingReceipt ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />} Download receipt (PDF)
              </Button>
              <Button onClick={resendReceipt} disabled={resendingReceipt} variant="outline" className="rounded-xl" data-testid="resend-receipt-btn">
                {resendingReceipt ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />} Resend receipt to customer
              </Button>
            </>
          )}
          {canRefund && (
            <Button onClick={() => setRefundModalOpen(true)}
              variant="outline" className="rounded-xl text-rose-600 hover:bg-rose-50 hover:text-rose-700 border-rose-200" data-testid="refund-btn">
              <RotateCcw className="w-4 h-4 mr-2" />
              Refund
            </Button>
          )}
        </div>
      </div>

      {booking.status === "pending_payment" && (
        <Card className="rounded-2xl p-5 border border-amber-200 bg-amber-50/60" data-testid="payment-reminder-card">
          <h3 className="font-display font-bold text-sm uppercase tracking-wide text-amber-800 mb-1">Payment pending</h3>
          <p className="text-sm text-[#4B5563] mb-3">This booking hasn't been paid yet. Resend the checkout link to the customer or copy it to share manually.</p>
          <div className="flex flex-wrap gap-2">
            <Button onClick={resendPaymentLink} disabled={resendingLink} className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="resend-payment-link-btn">
              {resendingLink ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />} Resend payment link to customer
            </Button>
            <Button onClick={copyPaymentLink} disabled={copyingLink} variant="outline" className="rounded-xl" data-testid="copy-payment-link-btn">
              {copyingLink ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Copy className="w-4 h-4 mr-2" />} Copy payment link
            </Button>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Customer + address */}
        <Card className="rounded-2xl p-6 border border-[#E5E7EB]">
          <h3 className="font-display font-bold mb-3">Customer</h3>
          <dl className="text-sm space-y-2">
            <div className="flex justify-between"><dt className="text-[#4B5563]">Name</dt><dd className="font-semibold">{booking.customer.full_name}</dd></div>
            <div className="flex justify-between"><dt className="text-[#4B5563]">Email</dt><dd className="font-semibold">{booking.customer.email}</dd></div>
            <div className="flex justify-between"><dt className="text-[#4B5563]">Phone</dt><dd className="font-semibold">{booking.customer.phone}</dd></div>
            <div className="flex justify-between gap-4"><dt className="text-[#4B5563]">Address</dt><dd className="font-semibold text-right">{booking.customer.address_line1}{booking.customer.address_line2 ? `, ${booking.customer.address_line2}` : ""}<br />{booking.customer.city}, {booking.customer.state} {booking.customer.zip_code}</dd></div>
            <div className="flex justify-between" data-testid="sms-consent-row">
              <dt className="text-[#4B5563]">SMS consent</dt>
              <dd className="font-semibold text-right">
                {booking.customer.sms_consent
                  ? (booking.customer.sms_opted_out_at
                    ? <span className="text-rose-600">Opted out {formatDateTime(booking.customer.sms_opted_out_at, locale)}</span>
                    : <span className="text-emerald-700">Yes — {formatDateTime(booking.customer.sms_consent_at, locale)}</span>)
                  : "No"}
              </dd>
            </div>
          </dl>
        </Card>

        {/* Service + pricing */}
        <Card className="rounded-2xl p-6 border border-[#E5E7EB]">
          <h3 className="font-display font-bold mb-3">Service</h3>
          <dl className="text-sm space-y-2">
            <div className="flex justify-between"><dt className="text-[#4B5563]">Zone</dt><dd className="font-semibold">{booking.service.zone_name}</dd></div>
            <div className="flex justify-between"><dt className="text-[#4B5563]">Property</dt><dd className="font-semibold capitalize">{booking.service.property_type}</dd></div>
            <div className="flex justify-between"><dt className="text-[#4B5563]">Size</dt><dd className="font-mono font-semibold">{booking.service.square_feet} sq ft</dd></div>
            {booking.service.property_type === "other" && booking.service.space_description && (
              <div className="flex justify-between gap-4 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2" data-testid="space-description-row">
                <dt className="text-amber-800 font-semibold">Space type</dt>
                <dd className="font-semibold text-right text-amber-900">{booking.service.space_description}</dd>
              </div>
            )}
            <div className="flex justify-between"><dt className="text-[#4B5563]">Rooms</dt><dd className="font-mono font-semibold">{booking.service.bedrooms} BR · {booking.service.bathrooms} BA · {booking.service.half_bathrooms} Half</dd></div>
            <div className="flex justify-between"><dt className="text-[#4B5563]">Preferred date</dt><dd className="font-semibold">{booking.service.preferred_date ? formatDate(booking.service.preferred_date, locale) : "—"} · <span>{timeSlotLabel(booking.service.preferred_time_slot, locale)}</span></dd></div>
            <div><dt className="text-[#4B5563] mb-1">Tasks</dt><dd className="font-semibold capitalize">{(booking.service.tasks || []).join(", ") || "—"}</dd></div>
            {booking.service.additional_notes && <div><dt className="text-[#4B5563] mb-1">Customer notes</dt><dd className="text-sm whitespace-pre-wrap p-3 rounded-xl bg-[#F5F5F4]">{booking.service.additional_notes}</dd></div>}
          </dl>
        </Card>

        <Card className="rounded-2xl p-6 border border-[#E5E7EB]">
          <h3 className="font-display font-bold mb-3">Pricing</h3>
          <dl className="text-sm space-y-2">
            <div className="flex justify-between"><dt className="text-[#4B5563]">{booking.service.property_type === "other" ? "Base (commercial)" : "Base"}</dt><dd className="font-mono">${booking.pricing.base_price.toFixed(2)}</dd></div>
            <div className="flex justify-between"><dt className="text-[#4B5563]">Size</dt><dd className="font-mono">${booking.pricing.sqft_surcharge.toFixed(2)}</dd></div>
            <div className="flex justify-between"><dt className="text-[#4B5563]">Bedrooms</dt><dd className="font-mono">${booking.pricing.bedroom_surcharge.toFixed(2)}</dd></div>
            <div className="flex justify-between"><dt className="text-[#4B5563]">Bathrooms</dt><dd className="font-mono">${(booking.pricing.bathroom_surcharge + booking.pricing.half_bathroom_surcharge).toFixed(2)}</dd></div>
            <div className="flex justify-between"><dt className="text-[#4B5563]">Tasks</dt><dd className="font-mono">${booking.pricing.tasks_surcharge.toFixed(2)}</dd></div>
            <div className="flex justify-between"><dt className="text-[#4B5563]">Zone</dt><dd className="font-mono">${booking.pricing.zone_surcharge.toFixed(2)}</dd></div>
            {booking.pricing.discount > 0 && (
              <div className="flex justify-between text-emerald-700"><dt>Discount{booking.pricing.discount_code ? ` (${booking.pricing.discount_code})` : ""}</dt><dd className="font-mono">−${booking.pricing.discount.toFixed(2)}</dd></div>
            )}
            <div className="flex justify-between border-t border-[#E5E7EB] pt-2 mt-2"><dt className="font-bold">Total</dt><dd className="font-mono text-xl font-bold text-[#0EA5A4]">${booking.pricing.total.toFixed(2)}</dd></div>
          </dl>
        </Card>

        <Card className="rounded-2xl p-6 border border-[#E5E7EB]">
          <h3 className="font-display font-bold mb-3">Payment</h3>
          <dl className="text-sm space-y-2">
            <div className="flex justify-between"><dt className="text-[#4B5563]">Session ID</dt><dd className="font-mono text-xs break-all">{booking.payment?.stripe_session_id || "—"}</dd></div>
            <div className="flex justify-between"><dt className="text-[#4B5563]">Paid at</dt><dd className="font-mono">{booking.payment?.paid_at ? formatDateTime(booking.payment.paid_at, locale) : "—"}</dd></div>
            <div className="flex justify-between"><dt className="text-[#4B5563]">Amount</dt><dd className="font-mono font-semibold">{booking.payment?.amount_paid ? `$${booking.payment.amount_paid.toFixed(2)}` : "—"}</dd></div>
          </dl>
        </Card>
      </div>

      {/* Commission breakdown + payout (Fase 3, Bloque B/D) */}
      {(booking.commission_split || ["paid", "assigned", "in_progress", "completed"].includes(booking.status)) && (
        <CommissionBreakdown booking={booking} locale={locale} onChanged={load} />
      )}

      {booking.refund && (
        <Card className="rounded-2xl p-6 border border-rose-200 bg-rose-50/40" data-testid="refund-history-section">
          <h3 className="font-display font-bold mb-3 flex items-center gap-2 text-rose-700"><RotateCcw className="w-4 h-4" /> Refund history</h3>
          <dl className="text-sm space-y-2">
            <div className="flex justify-between"><dt className="text-[#4B5563]">Amount refunded</dt><dd className="font-mono font-semibold" data-testid="refund-amount">${(booking.refund.amount_cents / 100).toFixed(2)}</dd></div>
            <div className="flex justify-between"><dt className="text-[#4B5563]">Refunded on</dt><dd className="font-semibold">{formatDateTime(booking.refund.refunded_at, locale)}</dd></div>
            <div className="flex justify-between"><dt className="text-[#4B5563]">Reason</dt><dd className="font-semibold capitalize">{booking.refund.reason.replace(/_/g, " ")}</dd></div>
            <div className="flex justify-between"><dt className="text-[#4B5563]">Refunded by</dt><dd className="font-semibold">{booking.refund.refunded_by}</dd></div>
            {booking.refund.admin_notes && <div><dt className="text-[#4B5563] mb-1">Notes</dt><dd className="text-sm whitespace-pre-wrap p-3 rounded-xl bg-white">{booking.refund.admin_notes}</dd></div>}
            <div className="flex justify-between items-center pt-1">
              <dt className="text-[#4B5563]">Stripe refund ID</dt>
              <dd className="font-mono text-xs">
                <a href={`https://dashboard.stripe.com/test/refunds/${booking.refund.stripe_refund_id}`} target="_blank" rel="noreferrer"
                  className="text-rose-700 hover:underline inline-flex items-center gap-1">{booking.refund.stripe_refund_id} <ExternalLink className="w-3 h-3" /></a>
              </dd>
            </div>
          </dl>
        </Card>
      )}

      {/* Status history timeline */}
      <Card className="rounded-2xl p-6 border border-[#E5E7EB]">
        <h3 className="font-display font-bold mb-4 flex items-center gap-2"><RefreshCw className="w-4 h-4" /> Status timeline</h3>
        <ol className="space-y-3">
          {(booking.status_history || []).map((s, i) => (
            <li key={i} className="flex items-start gap-3 text-sm">
              <span className="w-2 h-2 rounded-full bg-[#0EA5A4] mt-1.5 shrink-0" />
              <div>
                <div className="font-semibold capitalize">{s.status.replace("_", " ")}</div>
                <div className="text-[#9CA3AF] text-xs">{formatDateTime(s.at, locale)} {s.by ? `· by ${s.by}` : ""}</div>
              </div>
            </li>
          ))}
        </ol>
      </Card>

      {(booking.sms_log || []).length > 0 && (
        <Card className="rounded-2xl p-6 border border-[#E5E7EB]" data-testid="sms-log-card">
          <h3 className="font-display font-bold mb-4 flex items-center gap-2"><MessageSquareText className="w-4 h-4" /> SMS log</h3>
          <ol className="space-y-3">
            {booking.sms_log.map((s, i) => (
              <li key={i} className="flex items-start gap-3 text-sm" data-testid={`sms-log-row-${i}`}>
                <span className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${s.status === "sent" ? "bg-emerald-500" : s.status === "skipped" ? "bg-[#9CA3AF]" : "bg-rose-500"}`} />
                <div>
                  <div className="font-semibold capitalize">{s.kind.replace("_", " ")} — {s.status}</div>
                  <div className="text-[#9CA3AF] text-xs">{formatDateTime(s.at, locale)}{s.detail ? ` · ${s.detail}` : ""}</div>
                </div>
              </li>
            ))}
          </ol>
        </Card>
      )}

      {booking.checkin_timestamp && (
        <CheckInSection booking={booking} locale={locale} />
      )}

      {/* Work photos (before / after) — visible when the cleaner has been assigned */}
      {booking.cleaner_id && <AdminWorkPhotos bookingId={booking.id} />}

      {/* Assignment */}
      <Card className="rounded-2xl p-6 border border-[#E5E7EB]" data-testid="assignment-card">
        <h3 className="font-display font-bold mb-3 flex items-center gap-2"><UserCheck className="w-4 h-4" /> Assignment</h3>
        {booking.cleaner ? (
          <div>
            <AssignmentStatusBadge booking={booking} locale={locale} />
            <div className="flex items-start gap-4">
              <Avatar src={booking.cleaner.photo_url} name={booking.cleaner.full_name} size={64} />
              <div className="min-w-0 flex-1">
                <div className="font-display text-lg font-bold" data-testid="assigned-cleaner-name">{booking.cleaner.full_name}</div>
                {booking.cleaner.bio && <p className="text-sm text-[#4B5563] mt-1">{booking.cleaner.bio}</p>}
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {(booking.cleaner.specialties || []).map((s) => <Badge key={s} variant="outline" className="text-[10px]">{s}</Badge>)}
                </div>
                {(booking.cleaner.languages || []).length > 0 && (
                  <div className="text-xs text-[#9CA3AF] mt-1.5">Languages: {(booking.cleaner.languages || []).map((l) => l.toUpperCase()).join(", ")}</div>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-4">
              <Button onClick={notifyCustomerCleaner} disabled={notifying} className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="send-assignment-btn">
                {notifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Mail className="w-4 h-4 mr-2" /> Send assignment to cleaner</>}
              </Button>
              <Button onClick={() => setPickerOpen(true)} variant="outline" className="rounded-xl" data-testid="reassign-btn"><RotateCcw className="w-4 h-4 mr-2" /> Reassign</Button>
              <Button onClick={unassignCleaner} variant="outline" className="rounded-xl text-rose-600 border-rose-300 hover:bg-rose-50 hover:text-rose-700 font-semibold" data-testid="unassign-btn"><UserX className="w-4 h-4 mr-2" /> Unassign</Button>
            </div>
          </div>
        ) : (
          <div className="text-center py-6">
            {booking.declined_cleaner_name && (
              <div className="mb-4 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 text-sm px-4 py-3 inline-flex items-center gap-2" data-testid="declined-flag">
                <XCircle className="w-4 h-4" /> Declined by {booking.declined_cleaner_name}
              </div>
            )}
            <p className="text-sm text-[#4B5563] mb-3">No cleaner assigned yet.</p>
            <Button onClick={() => setPickerOpen(true)} className="rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white" data-testid="assign-cleaner-btn">
              <UserCheck className="w-4 h-4 mr-2" /> Assign a cleaner
            </Button>
          </div>
        )}
      </Card>

      <CleanerPicker open={pickerOpen} onClose={() => setPickerOpen(false)} cleaners={cleaners}
        booking={booking} assigning={assigning} onPick={assignCleaner} />

      <RefundModal open={refundModalOpen} onClose={() => setRefundModalOpen(false)} booking={booking} onRefunded={load} />

      {/* Internal notes */}
      <Card className="rounded-2xl p-6 border border-[#E5E7EB]">
        <h3 className="font-display font-bold mb-3">Internal notes</h3>
        <Textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)}
          className="rounded-xl" placeholder="Internal notes for the cleaning team (not shown to customer)…"
          data-testid="internal-notes" />
        <Button onClick={saveNotes} disabled={savingNotes}
          className="mt-3 rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white" data-testid="save-notes-btn">
          {savingNotes ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Save</>}
        </Button>
      </Card>
    </div>
  );
}


function AssignmentStatusBadge({ booking, locale }) {
  const status = booking.assignment_status;
  if (status === "accepted") {
    return (
      <div className="mb-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm px-3 py-2 inline-flex items-center gap-2" data-testid="assignment-status-accepted">
        <CheckCircle2 className="w-4 h-4" /> Accepted by {booking.cleaner?.full_name} {booking.assignment_accepted_at ? `on ${formatDateTime(booking.assignment_accepted_at, locale)}` : ""}
      </div>
    );
  }
  if (status === "awaiting_response") {
    return (
      <div className="mb-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm px-3 py-2 inline-flex items-center gap-2" data-testid="assignment-status-awaiting">
        <Clock className="w-4 h-4" /> Awaiting cleaner response
      </div>
    );
  }
  return null;
}


function CleanerPicker({ open, onClose, cleaners, booking, assigning, onPick }) {
  const bCountry = booking?.customer?.country;
  const bCity = (booking?.customer?.city || "").toLowerCase();
  // Sort: same city first, then same country, then the rest
  const ranked = [...(cleaners || [])].sort((a, b) => {
    const score = (c) => (c.city?.toLowerCase() === bCity ? 2 : 0) + (c.country === bCountry ? 1 : 0);
    return score(b) - score(a);
  });
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="cleaner-picker-modal">
        <DialogHeader><DialogTitle className="font-display text-2xl tracking-tighter">Assign a cleaner</DialogTitle></DialogHeader>
        {ranked.length === 0 ? (
          <p className="text-sm text-[#4B5563] py-8 text-center">No active cleaners. Add one in the Cleaners section first.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {ranked.map((c) => (
              <button key={c.id} disabled={assigning} onClick={() => onPick(c.id)} data-testid={`picker-cleaner-${c.id}`}
                className="text-left rounded-2xl border border-[#E5E7EB] p-4 hover:border-[#0EA5A4] hover:bg-[#0EA5A4]/[0.04] transition-colors disabled:opacity-50">
                <div className="flex items-center gap-3">
                  <Avatar src={c.photo_url} name={c.full_name} size={44} />
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{c.full_name}</div>
                    <div className="text-xs text-[#9CA3AF]">{[c.city, c.country].filter(Boolean).join(", ") || "—"}</div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {(c.specialties || []).slice(0, 4).map((s) => <Badge key={s} variant="outline" className="text-[10px]">{s}</Badge>)}
                </div>
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}



function CheckInSection({ booking, locale }) {
  // Fetch the check-in selfie as a blob so the bearer-token-protected endpoint
  // can serve it inline. Done once per mount per booking.
  const [selfieUrl, setSelfieUrl] = useState("");
  useEffect(() => {
    let active = true;
    let blobUrl = "";
    (async () => {
      if (!booking.checkin_selfie_path) return;
      try {
        const r = await fetch(`${API_BASE}/admin/bookings/${booking.id}/checkin-selfie`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        if (!r.ok) return;
        const blob = await r.blob();
        blobUrl = URL.createObjectURL(blob);
        if (active) setSelfieUrl(blobUrl);
      } catch { /* swallow — show empty state */ }
    })();
    return () => { active = false; if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [booking.id, booking.checkin_selfie_path]);

  const coords = booking.checkin_coordinates || {};
  const distance = booking.checkin_distance_to_address_meters;
  const far = booking.checkin_far_from_address;
  const checkin = (coords.lat != null && coords.lng != null) ? { lat: coords.lat, lng: coords.lng } : null;
  const address = (booking.address_lat != null && booking.address_lng != null)
    ? { lat: booking.address_lat, lng: booking.address_lng }
    : null;

  return (
    <Card className="rounded-2xl p-6 border border-[#E5E7EB]" data-testid="arrival-checkin-section">
      <h3 className="font-display font-bold mb-4 flex items-center gap-2">
        <MapPin className="w-4 h-4 text-[#0EA5A4]" /> Arrival check-in
      </h3>
      {far && (
        <div className="rounded-xl bg-amber-50 border border-amber-300 text-amber-900 px-4 py-3 text-sm mb-4 flex items-start gap-2" data-testid="checkin-far-warning">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <b>Possible mismatch.</b> The cleaner checked in {Math.round(distance)} m from the booking address.
            Review the selfie and contact them if needed.
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] gap-5">
        <div>
          <div className="text-[10px] uppercase tracking-wide text-[#9CA3AF] font-semibold mb-1.5">Check-in selfie</div>
          <div className="aspect-square rounded-lg overflow-hidden bg-[#F5F5F4] border border-[#E5E7EB] grid place-items-center" data-testid="checkin-selfie-tile">
            {selfieUrl ? (
              <img src={selfieUrl} alt="check-in selfie" className="w-full h-full object-cover" />
            ) : (
              <Loader2 className="w-4 h-4 animate-spin text-[#9CA3AF]" />
            )}
          </div>
          <dl className="text-xs mt-3 space-y-1">
            <div className="flex justify-between"><dt className="text-[#9CA3AF]">When</dt><dd className="font-mono">{formatDateTime(booking.checkin_timestamp, locale)}</dd></div>
            {distance != null && (
              <div className="flex justify-between" data-testid="checkin-distance">
                <dt className="text-[#9CA3AF]">Distance</dt>
                <dd className={`font-mono font-semibold ${far ? "text-amber-700" : "text-emerald-700"}`}>{Math.round(distance)} m</dd>
              </div>
            )}
            {coords.accuracy_meters && (
              <div className="flex justify-between"><dt className="text-[#9CA3AF]">GPS accuracy</dt><dd className="font-mono">±{Math.round(coords.accuracy_meters)} m</dd></div>
            )}
          </dl>
        </div>
        <div>
          <CheckInMap checkin={checkin} address={address} />
          {!address && (
            <p className="text-xs text-[#9CA3AF] mt-2">
              Booking address could not be geocoded. Distance not available.
            </p>
          )}
        </div>
      </div>
    </Card>
  );
}
